import type { TabDef } from './types.js'
import { isFieldRequired, isPresent, type ConditionContext } from './conditions.js'
import { visibleSectionFields } from './section-fields.js'
import type { DraftPayload, TabState } from './draft.js'
import { emptyTabState } from './draft.js'

export interface TabCompletion {
  tab: string
  required: number
  completed: number
  pct: number
  /** True when the tab was satisfied by a toggle rather than by data. */
  skipped: boolean
}
export interface CompletionResult {
  tabs: Record<string, TabCompletion>
  overallPct: number
}

function profileValues(payload: DraftPayload): Record<string, unknown> {
  const general = payload.tabs['general']
  if (!general) return {}
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(general.values)) {
    const field = k.includes('.') ? k.slice(k.indexOf('.') + 1) : k
    out[field] = v
  }
  return out
}

/**
 * Completion per spec FRM-06: completed required items over required items per tab;
 * a repeatable section counts as complete when it has at least one row or its toggle is on;
 * the overall score is the average of tab scores (PitchBook's formula).
 */
export function computeCompletion(tabs: TabDef[], payload: DraftPayload): CompletionResult {
  const profile = profileValues(payload)
  const result: Record<string, TabCompletion> = {}

  for (const tab of tabs) {
    const state: TabState = payload.tabs[tab.key] ?? emptyTabState()
    let required = 0
    let completed = 0
    let skipped = false

    for (const section of tab.sections) {
      if (section.kind === 'review') continue

      const toggleOn = section.toggle ? Boolean(state.flags[section.toggle.key]) : false

      if (section.kind === 'repeatable' || section.kind === 'uploads') {
        required += 1
        const rows = state.rows[section.key] ?? []
        const min = section.minRowsForComplete ?? 1
        if (toggleOn) {
          completed += 1
          skipped = true
        } else if (rows.length >= min) {
          completed += 1
        }
        // Required fields inside rows contribute one unit per row.
        if (!toggleOn && section.kind === 'repeatable') {
          for (const row of rows) {
            const ctx: ConditionContext = { values: row.values, controls: state.controls, profile }
            const rowRequired = visibleSectionFields(section, ctx).filter((f) => isFieldRequired(f, ctx))
            if (rowRequired.length === 0) continue
            required += 1
            const done = rowRequired.every((f) => isPresent(row.values[f.key]))
            if (done) completed += 1
          }
        }
        continue
      }

      const ctx: ConditionContext = {
        values: Object.fromEntries(
          Object.entries(state.values).map(([k, v]) => [k.includes('.') ? k.slice(k.indexOf('.') + 1) : k, v]),
        ),
        controls: state.controls,
        profile,
      }
      if (toggleOn) {
        // A "no changes" toggle satisfies the section's own required fields.
        const fields = visibleSectionFields(section, ctx).filter((f) => isFieldRequired(f, ctx))
        required += fields.length
        completed += fields.length
        if (fields.length > 0) skipped = true
        continue
      }
      for (const field of visibleSectionFields(section, ctx)) {
        if (!isFieldRequired(field, ctx)) continue
        required += 1
        if (isPresent(state.values[`${section.key}.${field.key}`])) completed += 1
      }
    }

    const pct = required === 0 ? 100 : Math.round((completed / required) * 100)
    result[tab.key] = { tab: tab.key, required, completed, pct, skipped }
  }

  const scored = Object.values(result)
  const overallPct = scored.length === 0 ? 0 : Math.round(scored.reduce((s, t) => s + t.pct, 0) / scored.length)
  return { tabs: result, overallPct }
}
